import React from 'react';
import { X, User, Phone, Mail, Calendar, Clock, Bed, LogIn, LogOut } from 'lucide-react';
import { format } from 'date-fns';
import { Booking } from '../types';
import { cn } from '../utils/cn';

interface TodaysGuestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  guests: Booking[];
  type: 'checkin' | 'checkout';
  title: string;
}

const TodaysGuestsModal: React.FC<TodaysGuestsModalProps> = ({
  isOpen,
  onClose,
  guests,
  type,
  title,
}) => {
  if (!isOpen) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'checked_in':
        return 'bg-green-100 text-green-800';
      case 'checked_out':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'checked_in':
        return <LogIn className="w-4 h-4" />;
      case 'checked_out':
        return <LogOut className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  return (
    <>
      <style>
        {`
          body {
            overflow: hidden;
            margin: 0;
            padding: 0;
          }
        `}
      </style>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          margin: 0, 
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          zIndex: 9999
        }}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" 
          style={{ 
            margin: 0,
            backgroundColor: 'white',
            borderRadius: '1rem'
          }}
        >
          {/* Header */}
          <div className={cn(
            "text-white p-6 flex items-center justify-between",
            type === 'checkin' ? "bg-green-500" : "bg-orange-500"
          )}>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                {type === 'checkin' ? (
                  <LogIn className="w-5 h-5 text-white" />
                ) : (
                  <LogOut className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{title}</h2>
                <p className="text-opacity-90">
                  {format(new Date(), 'MMM dd, yyyy')} • {guests.length} guest{guests.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
            {guests.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {type === 'checkin' ? (
                    <LogIn className="w-8 h-8 text-gray-400" />
                  ) : (
                    <LogOut className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No guests found</h3>
                <p className="text-gray-500">
                  {type === 'checkin' 
                    ? 'No guests are checking in today.' 
                    : 'No guests are checking out today.'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {guests.map((guest) => (
                  <div key={guest.bookingId} className="glass-card p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">{guest.guestName}</h3>
                            <p className="text-sm text-gray-600">Booking ID: {guest.bookingId}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            {guest.phone && (
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <Phone className="w-4 h-4" />
                                <span>{guest.phone}</span>
                              </div>
                            )}
                            {guest.email && (
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <Mail className="w-4 h-4" />
                                <span>{guest.email}</span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Bed className="w-4 h-4" />
                              <span>Room {guest.roomId} • {guest.roomType}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span>
                                {type === 'checkin' 
                                  ? `Check-in: ${format(new Date(guest.originalCheckinDate), 'MMM dd, yyyy')}`
                                  : `Check-out: ${format(new Date(guest.currentCheckoutDate), 'MMM dd, yyyy')}`
                                }
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1",
                              getStatusColor(guest.bookingStatus)
                            )}>
                              {getStatusIcon(guest.bookingStatus)}
                              <span>{guest.bookingStatus.replace('_', ' ').toUpperCase()}</span>
                            </span>
                            {guest.bookingSource && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                                {guest.bookingSource}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-gray-800">
                              ₹{guest.totalAmount.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Total Amount</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {guests.length} guest{guests.length !== 1 ? 's' : ''} for {format(new Date(), 'MMM dd, yyyy')}
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TodaysGuestsModal;
